package com.writerslab

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Top bar — single row on wide viewports, the file pill drops to its own
 * row on narrow ones. CSS for the breakpoint lives in `index.html` under
 * `.wl-file-group`.
 *
 * On wide: [Writers Lab]          [Lang][File][.txt][Credits][Hamb]
 * On narrow: [Writers Lab]        [Lang][Credits][Hamb]
 *                                            [File][.txt]
 */
@Composable
fun WritersLabTopBar(
    credits: Int,
    fileName: String?,
    wordCount: Int,
    isLoggedIn: Boolean,
    userName: String?,
    onDownloadTxt: (() -> Unit)?,
    onReset: (() -> Unit)?,
    onSignIn: () -> Unit,
    onSignOut: () -> Unit,
    onTopUp: () -> Unit,
    onHome: () -> Unit,
) {
    val s = LocalStrings.current
    var menuOpen by remember { mutableStateOf(false) }

    Div({
        style {
            bg(WLColors.SurfaceContainerLow)
            property("border-radius", "8px")
            property("padding", "10px 20px")
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("align-items", "center")
            property("gap", "8px")
            fillWidth()
            property("box-sizing", "border-box")
        }
    }) {
        // Brand block: rounded violet icon + wordmark, styled to mirror the
        // what2read.space header. Sits flush to the left; the controls
        // wrapper below claims the rest of the row via `margin-left: auto`.
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "10px")
                property("flex-shrink", "0")
                clickableCursor()
            }
            onClick { onHome() }
        }) {
            // Icon — same SVG as the parent what2read.space site for a
            // unified brand. Sized 32px to match the wordmark's cap height.
            org.jetbrains.compose.web.dom.Img(src = "logo_icon.svg") {
                attr("alt", "")
                attr("aria-hidden", "true")
                style {
                    property("width", "32px")
                    property("height", "32px")
                    property("display", "block")
                    property("border-radius", "8px")
                }
            }
            // Wordmark — styled to mirror the what2read wordmark: bold
            // sans-serif, tight tracking, mixed white + violet accent on
            // the second word.
            Span({
                style {
                    property("font-size", "22px")
                    property("font-weight", "700")
                    property("letter-spacing", "-0.01em")
                    property("white-space", "nowrap")
                    property("line-height", "1")
                    fg(WLColors.OnSurface)
                }
            }) {
                Text("Writers ")
                Span({ style { fg(WLColors.Primary) } }) { Text("Lab") }
            }
        }

        // Controls wrapper — language switcher, file-group, credits,
        // burger. `margin-left: auto` shoves the whole group to the
        // right edge of row 1 on wide; `flex-wrap` + `justify-content:
        // flex-end` keeps the items aligned right when the group
        // itself wraps onto row 2 on narrow viewports (no orphan chip
        // sitting alone on the left).
        Div({
            style {
                property("display", "flex")
                property("flex-wrap", "wrap")
                property("align-items", "center")
                property("gap", "8px")
                property("justify-content", "flex-end")
                property("margin-left", "auto")
            }
        }) {
        LanguageSwitcher()

        if (fileName != null && onReset != null) {
            // Single pill button — «Заменить книгу». Filename + word
            // count moved into the sidebar (QualityHeader), the top
            // bar now carries only the action the user actually needs
            // up here: «I want to upload a different book». `.txt`
            // download stays as a small adjacent pill.
            Div({
                classes("wl-file-group")
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "8px")
                }
            }) {
                Div({
                    style {
                        bg(WLColors.SurfaceContainerHigh)
                        property("border-radius", "120px")
                        property("padding", "8px 16px")
                        property("flex-shrink", "0")
                        clickableCursor()
                    }
                    onClick { onReset() }
                }) {
                    Span({
                        style {
                            typography(TextLevel.LabelLarge)
                            fg(WLColors.OnSurface)
                            property("white-space", "nowrap")
                        }
                    }) { Text(s.replaceBook) }
                }
                // .txt download is a debug-only affordance now: only
                // surfaces when Alt+Shift is physically held on a
                // non-touch device. Touch devices (matchMedia
                // «pointer: coarse») never show it — Alt+Shift doesn't
                // fire from on-screen keyboards anyway, but the explicit
                // touch check covers the «mobile + Bluetooth keyboard»
                // edge case. Same Alt+Shift hook the teaser low-quality
                // mode uses; no interference.
                val showTxt = onDownloadTxt != null
                    && rememberAltShiftHeld()
                    && remember { !kotlinx.browser.window.matchMedia("(pointer: coarse)").matches }
                if (showTxt) {
                    Div({
                        style {
                            bg(WLColors.SurfaceContainerHigh)
                            property("border-radius", "120px")
                            property("padding", "8px 12px")
                            property("flex-shrink", "0")
                            clickableCursor()
                        }
                        onClick { onDownloadTxt() }
                    }) {
                        Span({
                            style {
                                typography(TextLevel.LabelLarge)
                                fg(WLColors.OnSurfaceVariant)
                            }
                        }) { Text(".txt") }
                    }
                }
            }
        }

        if (isLoggedIn) {
            Div({
                style {
                    property("display", "flex")
                    property("gap", "6px")
                    property("align-items", "center")
                    bg(WLColors.SurfaceContainerHigh)
                    property("border-radius", "120px")
                    property("padding", "8px 14px")
                    property("flex-shrink", "0")
                    clickableCursor()
                }
                onClick { onTopUp() }
            }) {
                Span({
                    style {
                        typography(TextLevel.LabelLarge)
                        fg(WLColors.Secondary)
                    }
                }) { Text("$credits") }
                DragonIcon(sizePx = 18)
            }
        }

        if (isLoggedIn) {
            Div({
                attr("aria-label", s.menuAria)
                attr("aria-expanded", menuOpen.toString())
                style {
                    bg(WLColors.SurfaceContainerHigh)
                    property("border-radius", "120px")
                    property("padding", "10px 12px")
                    flexCol(gapPx = 3)
                    property("flex-shrink", "0")
                    clickableCursor()
                }
                onClick { menuOpen = !menuOpen }
            }) {
                repeat(3) {
                    Div({
                        style {
                            property("width", "18px")
                            property("height", "2px")
                            property("border-radius", "1px")
                            bg(WLColors.OnSurface)
                        }
                    })
                }
            }
        } else {
            Div({
                style {
                    bg(WLColors.PrimaryContainer)
                    property("border-radius", "120px")
                    property("padding", "8px 16px")
                    property("flex-shrink", "0")
                    clickableCursor()
                }
                onClick { onSignIn() }
            }) {
                Span({
                    style {
                        typography(TextLevel.LabelLarge)
                        fg(WLColors.OnPrimaryContainer)
                        property("white-space", "nowrap")
                    }
                }) { Text(s.signIn) }
            }
        }
        } // /controls wrapper
    }

    if (menuOpen) {
        Div({
            style {
                property("position", "fixed")
                property("inset", "0")
                property("z-index", "99")
            }
            onClick { menuOpen = false }
        })
        Div({
            style {
                property("position", "absolute")
                property("right", "16px")
                property("top", "60px")
                bg(WLColors.SurfaceContainerHigh)
                property("border-radius", "8px")
                property("padding", "8px")
                property("min-width", "200px")
                property("box-shadow", "0 4px 12px rgba(0, 0, 0, 0.4)")
                property("z-index", "100")
                flexCol(gapPx = 4)
            }
        }) {
            if (userName != null) {
                Div({
                    style {
                        typography(TextLevel.BodySmall)
                        fg(WLColors.OnSurfaceVariant)
                        property("padding", "8px 12px")
                    }
                }) { Text(userName) }
            }
            MenuItem(s.signOut) {
                menuOpen = false
                onSignOut()
            }
        }
    }
}

@Composable
private fun MenuItem(label: String, onClick: () -> Unit) {
    Div({
        style {
            typography(TextLevel.BodyMedium)
            fg(WLColors.OnSurface)
            property("padding", "8px 12px")
            property("border-radius", "6px")
            clickableCursor()
        }
        onClick { onClick() }
    }) {
        Text(label)
    }
}

private fun formatWordCount(n: Int): String {
    val s = n.toString()
    val out = StringBuilder()
    for ((i, c) in s.reversed().withIndex()) {
        if (i > 0 && i % 3 == 0) out.append(' ')
        out.append(c)
    }
    return out.reverse().toString()
}

@Composable
fun VerificationBanner(
    email: String?,
    cooldownSeconds: Int,
    onResend: () -> Unit,
    onConfirm: () -> Unit,
) {
    val s = LocalStrings.current
    val onCooldown = cooldownSeconds > 0
    Div({
        classes("wl-verification-banner")
        style {
            fillWidth()
            bg("rgba(90, 42, 72, 0.6)")
            property("border-radius", "8px")
            property("padding", "12px 20px")
            property("margin-top", "6px")
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("align-items", "center")
            property("gap", "12px")
            property("box-sizing", "border-box")
        }
    }) {
        Span({
            style {
                typography(TextLevel.BodyMedium)
                fg(WLColors.OnTertiaryContainer)
                property("flex", "1")
                property("min-width", "180px")
            }
        }) { Text(s.verificationBannerText(email ?: "")) }

        Div({
            classes("wl-verification-banner-buttons")
            style {
                flexRow(gapPx = 8, align = AlignItems.Center)
                property("flex-wrap", "wrap")
            }
        }) {
            Div({
                style {
                    bg(WLColors.OnTertiaryContainer)
                    property("border-radius", "120px")
                    property("padding", "8px 16px")
                    property("white-space", "nowrap")
                    property("opacity", if (onCooldown) "0.45" else "1")
                    property("transition", "opacity 0.2s")
                    if (!onCooldown) clickableCursor()
                }
                if (!onCooldown) onClick { onResend() }
            }) {
                Span({
                    style {
                        typography(TextLevel.LabelLarge)
                        fg(WLColors.TertiaryContainer)
                    }
                }) {
                    Text(if (onCooldown) s.verificationCooldownButton(cooldownSeconds) else s.verificationResend)
                }
            }

            Div({
                style {
                    border("1px", "solid", WLColors.OnTertiaryContainer)
                    property("border-radius", "120px")
                    property("padding", "8px 16px")
                    property("white-space", "nowrap")
                    clickableCursor()
                }
                onClick { onConfirm() }
            }) {
                Span({
                    style {
                        typography(TextLevel.LabelLarge)
                        fg(WLColors.OnTertiaryContainer)
                    }
                }) { Text(s.verificationConfirm) }
            }
        }
    }
}

/**
 * Shown while a payment the user left for hasn't settled — back from the
 * payment page, after Back, or on a later visit. [onResume] reopens the
 * same unpaid invoice; null when its page isn't known.
 */
@Composable
fun PendingPaymentBanner(onResume: (() -> Unit)?, onHide: () -> Unit) {
    val s = LocalStrings.current
    Div({
        classes("wl-payment-banner")
        style {
            fillWidth()
            bg(WLColors.PrimaryContainer)
            property("border-radius", "8px")
            property("padding", "12px 20px")
            property("margin-top", "6px")
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("align-items", "center")
            property("gap", "12px")
            property("box-sizing", "border-box")
        }
    }) {
        Span({
            style {
                typography(TextLevel.BodyMedium)
                fg(WLColors.OnPrimaryContainer)
                property("flex", "1")
                property("min-width", "180px")
            }
        }) { Text(s.paymentWaitingBanner) }

        Div({
            classes("wl-payment-banner-buttons")
            style {
                flexRow(gapPx = 8, align = AlignItems.Center)
                property("flex-wrap", "wrap")
            }
        }) {
            if (onResume != null) {
                Div({
                    style {
                        bg(WLColors.OnPrimaryContainer)
                        property("border-radius", "120px")
                        property("padding", "8px 16px")
                        property("white-space", "nowrap")
                        clickableCursor()
                    }
                    onClick { onResume() }
                }) {
                    Span({
                        style {
                            typography(TextLevel.LabelLarge)
                            fg(WLColors.PrimaryContainer)
                        }
                    }) { Text(s.paymentResume) }
                }
            }

            Div({
                style {
                    border("1px", "solid", WLColors.OnPrimaryContainer)
                    property("border-radius", "120px")
                    property("padding", "8px 16px")
                    property("white-space", "nowrap")
                    clickableCursor()
                }
                onClick { onHide() }
            }) {
                Span({
                    style {
                        typography(TextLevel.LabelLarge)
                        fg(WLColors.OnPrimaryContainer)
                    }
                }) { Text(s.close) }
            }
        }
    }
}

/**
 * Prominent banner shown to anonymous users once they've analysed a book:
 * signing in grants a one-time credit bonus plus a pool of free generations.
 * All counts come from [LocalPricing] so the copy tracks the real backend
 * quotas (the signup bonus is surfaced via /api/pricing).
 */
@Composable
fun SignupBonusBanner(onSignIn: () -> Unit) {
    val s = LocalStrings.current
    val p = LocalPricing.current
    // Shares the .wl-block-frame class with the surrounding result blocks so it
    // lines up (same width / max-width) and picks up the block-to-block 16px
    // rhythm from `.wl-block-frame + .wl-block-frame`; the inline styles override
    // the frame's neutral fill/border with the gold accent.
    Div({
        classes("wl-block-frame")
        style {
            bg("rgba(245, 185, 71, 0.12)")
            border("1px", "solid", "rgba(245, 185, 71, 0.5)")
            property("border-radius", "12px")
            property("padding", "12px 20px")
            property("display", "flex")
            property("flex-wrap", "wrap")
            property("align-items", "center")
            property("gap", "12px")
        }
    }) {
        Div({
            style {
                flexCol(gapPx = 3)
                property("flex", "1")
                property("min-width", "220px")
            }
        }) {
            Span({ style { typography(TextLevel.TitleSmall); fg(WLColors.OnSurface) } }) {
                Text(s.signupBonusTitle)
            }
            Span({ style { typography(TextLevel.BodyMedium); fg(WLColors.OnSurfaceVariant) } }) {
                Span({ style { fg(WLColors.Secondary); property("font-weight", "700") } }) {
                    Text("${p.signupBonusCredits} ")
                    DragonIcon(sizePx = 16, tint = DragonTint.GOLD)
                }
                Text(" " + s.signupBonusBody(
                    p.freeAnnotationsPerUser,
                    p.freeCoverBatchesPerUser,
                    p.freeAiHighlightPreviewsPerUser,
                ))
            }
        }
        Div({
            style {
                bg(WLColors.Secondary)
                property("border-radius", "120px")
                property("padding", "10px 22px")
                property("white-space", "nowrap")
                clickableCursor()
            }
            onClick { onSignIn() }
        }) {
            Span({ style { typography(TextLevel.LabelLarge); fg(WLColors.OnSecondary) } }) {
                Text(s.signIn)
            }
        }
    }
}

@Composable
private fun LanguageSwitcher() {
    val current = currentAppLocale()
    Div({
        style {
            flexRow(gapPx = 2, align = AlignItems.Center)
            property("flex-shrink", "0")
        }
    }) {
        SUPPORTED_LOCALES.forEach { locale ->
            val active = locale == current
            Div({
                attr("aria-pressed", active.toString())
                style {
                    typography(TextLevel.LabelSmall)
                    if (active) {
                        bg(WLColors.PrimaryContainer)
                        fg(WLColors.OnPrimaryContainer)
                    } else {
                        bg(WLColors.SurfaceContainerHigh)
                        fg(WLColors.OnSurfaceVariant)
                    }
                    property("border-radius", "120px")
                    property("padding", "6px 10px")
                    clickableCursor()
                }
                onClick { setAppLocale(locale) }
            }) {
                Text(locale.uppercase())
            }
        }
    }
}
